import type { Bar, Timeframe } from './types'
import { TF_SECONDS } from './types'

// Aggregate a base-timeframe series up to a higher timeframe.
// O = first, H = max, L = min, C = last, V = sum. Buckets are aligned to the
// UTC epoch (e.g. d1 buckets start at 00:00 UTC, h4 at 00/04/08/12/16/20 UTC).
//
// Assumes `base` is strictly ascending by time and is a LOWER timeframe than
// `target` (target seconds is a multiple of base seconds for clean alignment).

export function resample(base: Bar[], target: Timeframe): Bar[] {
  const period = TF_SECONDS[target]
  if (base.length === 0) return []

  const out: Bar[] = []
  let cur: Bar | null = null
  let curBucket = -1

  for (const b of base) {
    const bucket = Math.floor(b.time / period) * period
    if (cur === null || bucket !== curBucket) {
      if (cur) out.push(cur)
      curBucket = bucket
      cur = {
        time: bucket,
        open: b.open,
        high: b.high,
        low: b.low,
        close: b.close,
        volume: b.volume,
      }
    } else {
      if (b.high > cur.high) cur.high = b.high
      if (b.low < cur.low) cur.low = b.low
      cur.close = b.close
      cur.volume += b.volume
    }
  }
  if (cur) out.push(cur)
  return out
}

// For each display (higher-TF) bar, the ordered slice of base (lower-TF) bars
// that aggregate into it — used for intrabar TP/SL resolution: step on the
// display TF but resolve fills against the base bars in chronological order.
export function subBarsByDisplay(base: Bar[], display: Bar[], targetSec: number): Bar[][] {
  const map = new Map<number, Bar[]>()
  for (const b of base) {
    const bucket = Math.floor(b.time / targetSec) * targetSec
    const arr = map.get(bucket)
    if (arr) arr.push(b)
    else map.set(bucket, [b])
  }
  return display.map((d) => map.get(d.time) ?? [d])
}

/** Find the last index in `bars` whose time <= t (binary search). -1 if none. */
export function indexAtOrBefore(bars: Bar[], t: number): number {
  let lo = 0
  let hi = bars.length - 1
  let ans = -1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if (bars[mid]!.time <= t) {
      ans = mid
      lo = mid + 1
    } else {
      hi = mid - 1
    }
  }
  return ans
}
